import { FunctionalComponent, h } from '@stencil/core';
import classnames from 'classnames';

import { clickTypes } from 'reaxl-analytics-handlers';

import { caiEvent, getScaledImageSrc } from '@/utils';
import { ELOT_ANALYTICS } from '@/constants/elotEvents';
import { placeholderImages } from 'axl-config';

import Price from '../Price';
import Text from '../Text';
import LazyloadImage from '../LazyloadImage';
import CardProps from './types';
import DealIndicator from '../DealIndicator';
import OwnerDistance from '../OwnerDistance';
import { getListingMMT } from '../../utils/getListingMMT';

const ListingCard: FunctionalComponent<CardProps> = ({ inventory, brand, owner, index, whitelabel }) => {

    const rootClassName = classnames({
        'item-card': true,
        'row': true,
        'display-flex': true,
        'flex-column': true,
        'border-gray': true,
        'cursor-pointer': true,
    });

    const contentClassName = classnames({
        'item-card-content': true,
    });

    const {
        id,
        listingType = '',
        make,
        model,
        packages = [],
        pricingDetail,
        title,
        trim,
        website,
        year,
    } = inventory;

    // Placeholder image is a fallback if the listing has no image src
    // TODO: fallback image dimensions differ ever so slightly from our listing card. Lets return to this once the UI/UX updates are finished
    const scaledSrc = getScaledImageSrc(inventory) || placeholderImages.noImageAvailable;

    const handleClick = (event) => {
        caiEvent.send(ELOT_ANALYTICS, {
            action: 'inventoryClick', event, data: {
                inventoryId: id,
                indexClicked: index,
            }
        });

        if (website?.href) {
            window.location.href = website.href.replace('details.xhtml?listingId=', '/') + '?clickType=' + clickTypes.ELOT;
        }
    };

    const listingConditionYear = `${listingType.charAt(0).toUpperCase() + listingType.slice(1).toLowerCase()} ${year}`;
    const listingMMT = getListingMMT(make, model, trim);
    const listingPackages = packages.map(item => item.name).join('\xa0\xa0•\xa0\xa0');
    // TODO: For lines 59-61 & 62-64 to avoid wrapping div's around span elements add componentClass prop to Text component
    return (

        <div class="listing-card-container margin-horizontal-3">
            {/* Uncomment this div to add quick saves back in */}
            {/* <div class="save-overlay-container">
                <save-glyph listing={inventory} index={index} />
            </div> */}
            {pricingDetail?.dealIndicator && (
                <div class="deal-overlay-container">
                    <DealIndicator dealType={pricingDetail?.dealIndicator.toUpperCase()} whitelabel={whitelabel} />
                </div>
            )}
            <div
                data-cmp="itemCard"
                class={rootClassName}
                onClick={handleClick}
            >
                <div class={contentClassName}>
                    <div class="item-card-header image-height">
                        <LazyloadImage src={scaledSrc} fitImage={true} alt={title} />
                    </div>
                    <div class="item-card-body">
                        <div class="listing-content display-flex flex-column padding-horizontal-2 margin-top-2 text-left">
                            <div class="listing-content-text">
                                <Text weight="bold" component="div" size="200" antialiased={true} content={listingConditionYear} />
                                <Text weight="bold" component="div" size="400" antialiased={true} content={listingMMT} />
                                <Text component="div" size="200" color="subdued-lighter" antialiased={true} content={listingPackages} truncated={true} />
                            </div>
                            <Price
                                wrapperClass="listing-content-price margin-bottom-3"
                                msrp={pricingDetail?.msrp}
                                salePrice={pricingDetail?.salePrice || 0}
                                weight={brand === 'kbb' ? 'bold' : 'ultra-bold'}
                            />
                        </div>
                    </div>
                </div>
                {owner && (
                    <div class="item-card-footer display-flex flex-column col-sm-12 text-left">
                        <hr />
                        <OwnerDistance owner={owner} inventoryId={id} classNames="display-flex flex-column padding-vertical-1" />
                    </div>
                )}
            </div>
        </div>
    );
}

export default ListingCard;
